import React, { useEffect, useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import { AddShoppingCart, FavoriteBorder, LocationOn } from '@mui/icons-material';
import {
  Input,
  Badge,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CallIcon from '@mui/icons-material/Call';
import { Link } from 'react-router-dom'; // 💡 IMPORTANT: Router Link imported for /cart
import { useShop } from '../../context/ShopContext'; 
// --- AUTH IMPORTS ---
import { useAuth } from '../../context/AuthContext'; // Required to check login status
import UserProfileDropdown from './UserProfileDropdown'; // The dropdown component
import Login from './Login'; // Assuming this path is correct
// --- END AUTH IMPORTS ---

import Slogo from '../../assests/Slogo.png'; // Assuming this path is correct


// Define a type for categories
interface Category {
  title: string;
  href: string; // Direct link for the main category
}

const categories: Category[] = [
  { title: "EARRINGS", href: "/earrings" },
  { title: "NECKLACES", href: "/necklaces" },
  { title: "BANGLES", href: "/bangles" },
  { title: "CHAINS", href: "/chains" },
  { title: "RINGS", href: "/rings" },
  { title: "PENDANTS", href: "/pendants" },
  { title: "BRACELETS", href: "/bracelets" },
  { title: "COINS & BARS", href: "/coins-bars" },
  { title: "ALL JEWELLERY", href: "/all-jewellery" },
  { title: "COLLECTIONS", href: "/collections" },
];

// Helper Component for the marquee text (Vertical Ticker)
const VerticalTicker: React.FC = () => {
    // Ticker mein dikhane ke liye messages ka array
    const messages = [
        "✨ FREE shipping on orders of 6 items or more ! ✨",
        "🎁 Get 10% OFF on your first purchase !",
        "🔥 Exclusive Diwali Collections are LIVE now!",
    ];

    const displayMessages = [...messages, messages[0] , messages[1]]; 
    const [activeIndex, setActiveIndex] = useState(0);
    const [transitionDuration, setTransitionDuration] = useState('duration-700'); 
    const intervalTime = 3000; 
    const slideDuration = 800; 
    const itemHeight = 24;
    const translateY = `-${activeIndex * itemHeight}px`;

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveIndex((current) => {
                const nextIndex = current + 1;
                if (nextIndex === displayMessages.length - 1) {
                    setTimeout(() => {
                        setTransitionDuration('duration-0');
                        setActiveIndex(0);
                        setTimeout(() => {
                            setTransitionDuration('duration-700');
                        }, 50); 
                    }, slideDuration); 
                    return nextIndex; 
                }
                return nextIndex % displayMessages.length;
            });
        }, intervalTime); 
        return () => clearInterval(interval);
    }, [displayMessages.length]); 

    return (
        <div className="text-center font-semibold text-yellow-300 overflow-hidden h-6 flex items-center">
            <div 
                className={`w-full space-y-0 transition-transform ${transitionDuration} ease-in-out`} 
                style={{ transform: `translateY(${translateY})` }} 
            >
               {displayMessages.map((msg, index) => (
                   <span 
                        key={index} 
                        className="block h-6 leading-6 whitespace-nowrap"
                    >
                        {msg}
                    </span>
               ))}
            </div>
        </div>
    );
};


const Navbar: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [showLogin, setShowLogin] = useState<boolean>(false);
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));
  
  // 🎯 FIX 1 & Cart Count: ShopContext से cart items लेना
  const { cartItems = [], wishlistItems = [] } = useShop() || {}; 
  
  // 🎯 FIX 1: useAuth से केवल 'user' लें और isLoggedIn खुद से बनाएँ
  // (Assuming 'useAuth' hook exists in '../../context/AuthContext')
  const { user } = useAuth(); 
  const isLoggedIn = !!user; // This safely checks if 'user' is null or an object

  const googleMapsLink = "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent("Sardar Jewellers, Kalyanpur, Kanpur, UP");

  return (
    <header className='sticky top-0 z-50 w-full z-50 bg-transparent'>
      
      {/* Top Bar */}
      <div className="bg-amber-950 text-white p-2 px-4 text-xs md:text-sm grid grid-cols-3 items-center relative z-10 border-b border-amber-800">

        <div className="flex items-center gap-2 justify-start">
          <LocationOn sx={{ fontSize: 18, color: "gold" }} />
          <a
            href={googleMapsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline cursor-pointer flex items-center gap-1"
            aria-label="View address on Google Maps"
          >
            Address :
            <span className='whitespace-nowrap'>Kalyanpur, Kanpur, UP</span>
          </a>
        </div>
        
        <div className='flex justify-center'>
            <VerticalTicker />
        </div>

        <div className="flex items-center gap-3 justify-end whitespace-nowrap">
          <CallIcon  sx={{ fontSize: 18 ,color: "gold" }}/>
          <h2>  Contact Us : +91 8441856072</h2>
        </div>

      </div>

      {/* Middle Bar - Logo, Search, and Action Icons */}
      <Box className='bg-white shadow-sm relative z-10'>
        <div className='flex justify-between items-center px-3 lg:px-8 h-[75px] border-b'>
          
          <Link to="/" className='flex items-center gap-3 cursor-pointer'> 
            {!isLarge && (
              <IconButton onClick={() => setDrawerOpen(true)} aria-label="open mobile menu">
                <MenuIcon />
              </IconButton>
            )}
            <img src={Slogo} alt='Sardar Jewellers Logo' className='w-16 h-16' />
            <h1 className='logo text-base md:text-2xl text-primary-color'>Sardar Jewellers</h1>
          </Link> 

          <div className=" md:flex items-center gap-4">
            
            {/* Wishlist Icon */}
            {/* <IconButton aria-label="wishlist">
                <Badge badgeContent={wishlistItems.length} color="primary">
                  <FavoriteBorder />
                </Badge>
            </IconButton> */}

            {/* 🎯 ADD TO CART Icon and Link */}
             <Link to="/cart"> 
                <IconButton aria-label="shopping cart">
                    {/* Badge shows the total number of items in the cart */}
                    <Badge badgeContent={cartItems.length} color="primary">
                      <AddShoppingCart />
                    </Badge>
                </IconButton>
              </Link>

            {/* Login/User Section: Conditional Rendering */}
            <div className="flex items-center gap-2">
                {isLoggedIn ? (
                    // 1. Logged In: Call without prop to avoid TS error, use context internally
                    <UserProfileDropdown /> 
                ) : (
                    // 2. Logged Out: Show Login Button/Icon
                   <button
                    onClick={() => setShowLogin(true)}
                    className="flex items-center space-x-1 
                      text-white bg-[#00927c] 
                      transition-all duration-300 
                      p-2 rounded-lg 
                      hover:bg-transparent 
                      hover:border hover:border-[#00927c] 
                      hover:text-gray-700"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"></path></svg>
                        <span className="hidden sm:inline font-medium">Login</span>
                    </button>
                )}
            </div>
          </div>
        </div>
      </Box>

      {/* Navigation Links Bar (Desktop Only) */}
      {isLarge && (
        <nav className=" flex justify-center gap-8 p-3 text-sm font-medium bg-white shadow-md border-b">
          {categories.map((cat, idx) => (
            <a
              key={idx}
              href={cat.href} // Direct link
              className="px-2 py-1 cursor-pointer
                         hover:text-[#8B5E3C]
                         border-b-2 border-transparent hover:border-[#8B5E3C]
                         transition-colors duration-200 ease-in-out"
            >
              {cat.title}
            </a>
          ))}
        </nav>
      )}

      {/* Mobile Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 250 }} role="presentation" onClick={() => setDrawerOpen(false)}>
          <List>
            <div className="p-4 flex items-center border-b mb-2">
              <img src={Slogo} alt='Sardar Jewellers Logo' className='w-12 h-12 mr-2' />
              <h2 className='text-lg font-bold text-primary-color'>Sardar Jewellers</h2>
            </div>
            
            {categories.map((cat, idx) => (
              <ListItem key={idx} component="a" href={cat.href} disablePadding>
                <ListItemText primary={cat.title} className="px-4 py-2 font-medium" />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Login Modal */}
      {showLogin && <Login open={true} onClose={() => setShowLogin(false)} />}
    </header> 
  );
};

export default Navbar;